/**
 * Single-record store for an in-progress / paused upload.
 *
 * Persisted under `localStorage.pendingUpload` (one record per device — the
 * creator app never has more than one upload going at once, so a registry
 * would be over-design). On a return visit, if a non-expired record exists
 * and the route account matches, the Resume sheet (Phase 5) prompts the user
 * to re-pick the same video to continue.
 *
 * Constraints driving the schema:
 *  - GCS resumable session URIs expire 7 days after creation → we cap the
 *    record's usable lifetime at 6.5 days to leave a safety margin.
 *  - iOS Safari ITP wipes script-writable storage after 7 days of Safari use
 *    without site interaction — outside our control; record may simply be
 *    absent on return.
 *  - Safari does not expose persistent FileSystemFileHandles, so we store a
 *    file fingerprint (name/size/lastModified/type) and verify it on the
 *    user's re-pick. No content hashing — three-tuple collision probability
 *    for distinct user videos is effectively zero.
 *  - We do NOT persist the binary. Re-picking the same file is the
 *    supported recovery path.
 */

export const PENDING_UPLOAD_KEY = 'pendingUpload';
export const LEGACY_UPLOAD_DATA_KEY = 'uploadData';
export const LEGACY_UPLOADED_DATA_KEY = 'uploadedData';

/** 6.5 days in ms — buffer under the GCS 7-day session URI TTL. */
export const PENDING_UPLOAD_MAX_AGE_MS = 6.5 * 24 * 60 * 60 * 1000;

export type PendingUploadStatus = 'in_progress' | 'paused_wifi' | 'finalizing';

export interface PendingUploadFileFingerprint {
  name: string;
  size: number;
  lastModified: number;
  type: string;
}

export interface PendingUpload {
  /** Stable per-session UUID; matches `Upload.id` for the active instance. */
  id: string;
  /** GCS resumable session URI — usable until its 7-day TTL elapses. */
  uploadUrl: string;
  /** Account this upload belongs to. Resume offer is suppressed for mismatches. */
  accountId: string;
  /** Route slug at upload start, if any. Informational. */
  slug: string | null;
  /** File-identity fingerprint for re-pick verification. */
  file: PendingUploadFileFingerprint;
  /** Mime type echoed for convenience (also in `file.type`). */
  mimeType: string;
  /** Telemetry attempt_id of the run that wrote this record (for joins). */
  attemptId?: string | null;
  /** Bytes the client believes are committed server-side. */
  uploadedBytes: number;
  /** Total file size (mirror of file.size, kept for clarity in tooling). */
  totalBytes: number;
  /** Current chunk size; persists adaptive sizing across sessions. */
  chunkSize: number;
  /** Smoothed Mbps from the prior session. Null if no chunks completed. */
  mbpsEwma: number | null;
  /** Wall-clock ms of session creation. Drives the 6.5-day expiry. */
  createdAt: number;
  /** Last write timestamp. */
  updatedAt: number;
  /** Phase of the upload at the moment of the last write. */
  status: PendingUploadStatus;
}

const safeGetStorage = (): Storage | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

export const read = (): PendingUpload | null => {
  const storage = safeGetStorage();
  if (!storage) return null;
  const raw = storage.getItem(PENDING_UPLOAD_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as PendingUpload;
    // Cheap shape validation — if something looks off, treat as absent.
    if (!parsed.id || !parsed.uploadUrl || !parsed.file || typeof parsed.uploadedBytes !== 'number') {
      clear();
      return null;
    }
    return parsed;
  } catch {
    clear();
    return null;
  }
};

export const write = (record: PendingUpload): void => {
  const storage = safeGetStorage();
  if (!storage) return;
  try {
    storage.setItem(PENDING_UPLOAD_KEY, JSON.stringify(record));
  } catch {
    // QuotaExceededError or serialization failure — best-effort. If we can't
    // persist we still want the upload to proceed, just without resume.
  }
};

/** Apply a partial patch over the existing record. No-op if no record. */
export const patch = (partial: Partial<PendingUpload>): void => {
  const current = read();
  if (!current) return;
  write({ ...current, ...partial, updatedAt: Date.now() });
};

export const clear = (): void => {
  const storage = safeGetStorage();
  if (!storage) return;
  try {
    storage.removeItem(PENDING_UPLOAD_KEY);
  } catch {
    /* ignore */
  }
};

export const isExpired = (
  record: PendingUpload,
  maxAgeMs: number = PENDING_UPLOAD_MAX_AGE_MS,
  now: number = Date.now(),
): boolean => now - record.createdAt > maxAgeMs;

/**
 * One-shot migration: drop the legacy `uploadData` / `uploadedData` keys used
 * by the old `Upload.ts` localStorage helpers. Safe to call repeatedly.
 */
export const migrateLegacyKeys = (): void => {
  const storage = safeGetStorage();
  if (!storage) return;
  try {
    storage.removeItem(LEGACY_UPLOAD_DATA_KEY);
    storage.removeItem(LEGACY_UPLOADED_DATA_KEY);
  } catch {
    /* ignore */
  }
};
